import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { ENV } from '../utils/env.js';
import { getIntegrationEndpoint } from '../config/integrationConfig';
import { generateGitHubFindings, generateS3Findings, generateActiveDirectoryFindings } from '../utils/mockData';

// Central helper: extract backend error message consistently
const extractBackendMessage = (error, fallback = 'Something went wrong. Please try again.') => {
  try {
    return (
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      fallback
    );
  } catch (_) {
    return fallback;
  }
}

/**
 * Hook for managing integration findings data, filtering, pagination, and selection
 * @param {string} integrationType - The type of integration (s3, repositories, etc.)
 * @param {string} integrationId - The ID of the specific integration instance
 * @param {object} integrationConfig - The configuration object for this integration type
 * @param {function} showToast - Toast notification function
 * @param {object} filtersHook - Optional filters hook for backend filtering
 */
export const useIntegrationFindings = (integrationType, integrationId, integrationConfig, showToast, filtersHook = null) => {
  // Core data state
  const [findings, setFindings] = useState([]);
  const [filteredFindings, setFilteredFindings] = useState([]);
  const [allFindings, setAllFindings] = useState([]); // Store all findings for accurate filter counts
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [integration, setIntegration] = useState(null);
  const [name, setName] = useState('');

  // Filter state
  const [filterType, setFilterType] = useState('all'); // 'all', 'verified', 'unverified'
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all', or specific detector_type
  const [showSourceDropdown, setShowSourceDropdown] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    counts: { active: 0, all: 0, ignored: 0, unvalidated: 0 },
    detector_types: [],
    total_count: 0,
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    has_next: false,
    last_page: 1,
    page: 1,
    per_page: 50,
    total: 0,
  });

  // Selection state
  const [selected, setSelected] = useState({});
  const [rowErrors, setRowErrors] = useState({});
  const [revealedRows, setRevealedRows] = useState({}); // Track which rows are revealed

  // Refs
  const selectAllCheckboxRef = useRef(null);

  // Selection helpers
  const visibleIds = (filteredFindings || []).map(f => f.id || String(f.scan_id || ''));
  const allSelected = visibleIds.length > 0 && visibleIds.every(id => selected[id]);
  const selectedCount = Object.entries(selected).filter(([id, v]) => v && visibleIds.includes(id)).length;
  const isIndeterminate = selectedCount > 0 && !allSelected;

  const selectedFindings = () => (filteredFindings || []).filter(f => selected[f.id || String(f.scan_id || '')]);

  // Helper function to extract finding ID for UI state management
  const getFindingId = (finding, index) => {
    return finding.id || String(finding.scan_id || index || '');
  };

  // Selection actions
  const toggleSelect = (id) => {
    setSelected(prev => {
      const newSelected = { ...prev, [id]: !prev[id] };
      if (prev[id] && !newSelected[id]) {
        setRowErrors(prevErrors => {
          const newErrors = { ...prevErrors };
          delete newErrors[id];
          return newErrors;
        });
      }
      return newSelected;
    });
  };

  const clearRowErrors = (rowIds = null) => {
    if (rowIds === null) {
      setRowErrors({});
    } else {
      setRowErrors(prev => {
        const next = { ...prev };
        rowIds.forEach(id => delete next[id]);
        return next;
      });
    }
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected(prev => {
        const next = { ...prev };
        visibleIds.forEach(id => { delete next[id]; });
        return next;
      });
      clearRowErrors(visibleIds);
    } else {
      setSelected(prev => {
        const next = { ...prev };
        visibleIds.forEach(id => { next[id] = true; });
        return next;
      });
    }
  };

  const toggleReveal = (rowId) => {
    setRevealedRows(prev => ({
      ...prev,
      [rowId]: !prev[rowId]
    }));
  };

  // Filter actions
  const handleFilterChange = (type) => {
    setFilterType(type);
  };

  const handleSourceFilterChange = (source) => {
    setSourceFilter(source);
  };

  const getUniqueSources = useCallback(() => {
    if (!findings.data || findings.data.length === 0) {
      return [];
    }

    let filteredFindings = findings.data;
    if (filterType === 'verified') {
      filteredFindings = filteredFindings.filter(finding => finding.verified === true);
    } else if (filterType === 'unverified') {
      filteredFindings = filteredFindings.filter(finding => finding.verified === false);
    }

    // Get unique detector types that have findings for the current status
    const uniqueSources = [...new Set(filteredFindings.map(finding => finding.detector_type))];
    return uniqueSources.sort();
  }, [findings.data, filterType]);

  // Pagination actions
  const handlePrevPage = () => setPagination((p) => ({ ...p, page: Math.max(1, p.page - 1) }));
  const handleNextPage = () => setPagination((p) => ({ ...p, page: p.has_next ? p.page + 1 : p.page }));
  const handlePerPageChange = (perPage) => setPagination((p) => ({ ...p, per_page: perPage, page: 1 }));


  // Fetch integration data using config endpoints
  const fetchIntegrationData = useCallback(async () => {
    if (!integrationConfig) {
      setError('Invalid integration type');
      setLoading(false);
      return;
    }

    // For S3, GitHub, and Active Directory, use mock data only - skip API calls
    if (integrationType === 's3' || integrationType === 'github' || integrationType === 'active-directory') {
      // Use cached name if available, otherwise use integrationId
      const cacheKey = `integration_name_${integrationType}_${integrationId}`;
      try {
        const cachedData = sessionStorage.getItem(cacheKey);
        if (cachedData) {
          const { name: cachedName } = JSON.parse(cachedData);
          if (cachedName) {
            setName(cachedName);
            return;
          }
        }
      } catch (cacheErr) {
        console.warn('Error reading cached integration name:', cacheErr);
      }
      
      // Set name from integrationId for mock data
      setName(integrationId);
      return;
    }

    try {
      setLoading(true);
      
      // Check sessionStorage for cached name first
      const cacheKey = `integration_name_${integrationType}_${integrationId}`;
      try {
        const cachedData = sessionStorage.getItem(cacheKey);
        if (cachedData) {
          const { name: cachedName } = JSON.parse(cachedData);
          if (cachedName) {
            // Use cached name immediately for better UX
            setName(cachedName);
          }
        }
      } catch (cacheErr) {
        // If cache parsing fails, continue with API call
        console.warn('Error reading cached integration name:', cacheErr);
      }
      
      // Get the details endpoint from config
      const detailsEndpoint = getIntegrationEndpoint(integrationType, 'details', { id: integrationId });
      if (!detailsEndpoint) {
        // If no details endpoint, use cached name or integrationId
        setName(prev => prev || integrationId);
        return;
      }

      // Fetch integration details (even if we have cached data, to keep it fresh)
      const integrationResponse = await axios.get(
        `${ENV.API_URL}${detailsEndpoint}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('jwt')}`
          }
        }
      );

      setIntegration(integrationResponse.data);
      const freshName = integrationResponse.data?.name || integrationId;
      setName(freshName);
      
      // Update cache with fresh data
      if (freshName !== integrationId) {
        try {
          const cacheData = {
            name: freshName,
            timestamp: Date.now()
          };
          sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
        } catch (cacheErr) {
          console.warn('Error updating integration name cache:', cacheErr);
        }
      }
      
    } catch (err) {
      console.error('Error fetching integration data:', err);
      // Use cached name if available, otherwise fall back to integrationId
      setName(prev => prev || integrationId);
    }
  }, [integrationType, integrationId, integrationConfig]);

  // Effects - Split to avoid infinite loops
  
  // Initial load when integration changes
  useEffect(() => {
    if (integrationType && integrationId && integrationConfig) {
      fetchIntegrationData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationType, integrationId, integrationConfig]);

  // Load findings when filters or pagination change
  useEffect(() => {
    if (!integrationType || !integrationId || !integrationConfig) return;
    
    const loadFindingsData = async () => {
      try {
        setLoading(true);
        
        // Build filters using the filters hook if available, otherwise use inline filters
        let filters = {};
        
        if (filtersHook) {
          // Use the filters hook to build backend filter request
          filters = filtersHook.buildFilterRequest();
        } else {
          // Fallback to inline filter building (legacy)
          if (filterType === 'verified' || filterType === 'unverified') {
            const conditions = [];
            
            if (filterType === 'verified') {
              conditions.push({
                field: "verified",
                operator: "eq",
                value: "true"
              });
            } else if (filterType === 'unverified') {
              conditions.push({
                field: "verified",
                operator: "eq",
                value: "false"
              });
            }
            
            if (conditions.length > 0) {
              filters.groups = [
                {
                  conditions: conditions,
                  operator: "AND"
                }
              ];
            }
          }
        }
        
        // Get the findings endpoint from config
        const findingsEndpoint = getIntegrationEndpoint(integrationType, 'findings', { id: integrationId });
        if (!findingsEndpoint) {
          throw new Error('Findings endpoint not configured for this integration type');
        }

        let endpointUrl, requestMethod, requestBody;
        
        // Handle different endpoint configurations
        if (typeof findingsEndpoint === 'object' && findingsEndpoint.url) {
          endpointUrl = findingsEndpoint.url;
          requestMethod = findingsEndpoint.method || 'GET';
          
          if (requestMethod === 'POST') {
            requestBody = {
              pagination: {
                page: pagination.page,
                per_page: pagination.per_page
              }
            };
            
            if (Object.keys(filters).length > 0) {
              requestBody.filters = filters;
            }
          }
        } else {
          endpointUrl = findingsEndpoint;
          requestMethod = 'GET';
        }

        const requestConfig = {
          method: requestMethod,
          url: `${ENV.API_URL}${endpointUrl}`,
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem('jwt')}`,
          },
        };

        if (requestMethod === 'POST' && requestBody) {
          requestConfig.data = requestBody;
        } else if (requestMethod === 'GET') {
          requestConfig.params = {
            page: pagination.page,
            per_page: pagination.per_page
          };
        }

        // For S3, GitHub, and Active Directory, use mock data only - skip API calls
        if (integrationType === 'github' || integrationType === 's3' || integrationType === 'active-directory') {
            // Generate mock findings
            let allMockFindings = [];
            if (integrationType === 'github') {
              // Extract owner and repo from integration object, name, or integrationId
              // Use integrationId as primary seed to ensure uniqueness per integration
              let repoOwner = 'unknown';
              let repoName = integrationId;
              
              // Try to get from integration object first
              if (integration?.owner && integration?.name) {
                repoOwner = integration.owner;
                repoName = integration.name;
              } else if (integration?.full_name && integration.full_name.includes('/')) {
                const parts = integration.full_name.split('/');
                repoOwner = parts[0];
                repoName = parts[1];
              } else if (name && name.includes('/')) {
                const parts = name.split('/');
                repoOwner = parts[0];
                repoName = parts[1];
              } else if (name) {
                repoName = name;
              }
              
              // Always use integrationId in the seed to ensure uniqueness
              // Format: integrationId-owner-repoName to ensure each integration gets unique findings
              allMockFindings = generateGitHubFindings(integrationId, repoOwner, repoName, 75);
            } else if (integrationType === 's3') {
              // Use integrationId as primary identifier to ensure unique findings per bucket
              const bucketName = integration?.name || integration?.bucket_name || name || integrationId || `bucket-${integrationId}`;
              // Always use integrationId in the seed to ensure uniqueness
              allMockFindings = generateS3Findings(integrationId, bucketName, 75);
            } else if (integrationType === 'active-directory') {
              // Use integrationId as primary identifier to ensure unique findings per domain
              const domainName = integration?.name || name || integrationId || `domain-${integrationId}`;
              
              // For the first domain (ad.acaws.silverfort.io), use exact 6 findings
              if (integrationId === 'mock-domain-1' || domainName === 'ad.acaws.silverfort.io') {
                allMockFindings = [
                  {
                    id: 'finding-ad-mock-domain-1-1',
                    account_email: 'bind@ad.acaws.silverfort.io',
                    source_count: 1,
                    destination_count: 1,
                    auth_count: 5112,
                    risk_level: 'Low',
                    predictability: 'High',
                    verified: true,
                    created_at: new Date('2024-01-01').toISOString(),
                  },
                  {
                    id: 'finding-ad-mock-domain-1-2',
                    account_email: 'svc_drbackup@ad.acaws.silverfort.io',
                    source_count: 7,
                    destination_count: 4,
                    auth_count: 101130,
                    risk_level: 'Medium',
                    predictability: 'High',
                    verified: true,
                    created_at: new Date('2024-01-02').toISOString(),
                  },
                  {
                    id: 'finding-ad-mock-domain-1-3',
                    account_email: 'intranet-svc@ad.acaws.silverfort.io',
                    source_count: 6,
                    destination_count: 4,
                    auth_count: 192,
                    risk_level: 'Low',
                    predictability: 'Low',
                    verified: true,
                    created_at: new Date('2024-01-03').toISOString(),
                  },
                  {
                    id: 'finding-ad-mock-domain-1-4',
                    account_email: 'svc_app01@ad.acaws.silverfort.io',
                    source_count: 3,
                    destination_count: 3,
                    auth_count: 41,
                    risk_level: 'Low',
                    predictability: 'Low',
                    verified: true,
                    created_at: new Date('2024-01-04').toISOString(),
                  },
                  {
                    id: 'finding-ad-mock-domain-1-5',
                    account_email: 'svc_file_manager@ad.acaws.silverfort.io',
                    source_count: 6,
                    destination_count: 7,
                    auth_count: 1688,
                    risk_level: 'Low',
                    predictability: 'Low',
                    verified: true,
                    created_at: new Date('2024-01-05').toISOString(),
                  },
                  {
                    id: 'finding-ad-mock-domain-1-6',
                    account_email: 'MSOL_14027b699981',
                    source_count: 1,
                    destination_count: 0,
                    auth_count: 0,
                    risk_level: 'High',
                    predictability: 'Low',
                    verified: false,
                    created_at: new Date('2024-01-06').toISOString(),
                  },
                ];
              } else {
                // For other domains, generate 30-40 findings (deterministic based on domainId)
                const hash = integrationId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
                const findingsCount = 30 + (hash % 11); // Deterministic count between 30-40
                allMockFindings = generateActiveDirectoryFindings(integrationId, domainName, findingsCount);
              }
            }

            // Store all findings for filter count calculation
            setAllFindings(allMockFindings);

            // Apply filters to mock data
            let filteredMockFindings = [...allMockFindings];
            
            if (filtersHook) {
              const filterType = filtersHook.filterType || 'all';
              const sourceFilter = filtersHook.sourceFilter || 'all';
              
              if (filterType === 'verified') {
                filteredMockFindings = filteredMockFindings.filter(f => f.verified === true);
              } else if (filterType === 'unverified') {
                filteredMockFindings = filteredMockFindings.filter(f => f.verified === false);
              } else if (filterType === 'ignored') {
                filteredMockFindings = filteredMockFindings.filter(f => f.status === 'ignore');
              }
              
              if (sourceFilter !== 'all') {
                filteredMockFindings = filteredMockFindings.filter(f => f.detector_type === sourceFilter);
              }
            } else {
              // Legacy client-side filtering
              if (filterType === 'verified') {
                filteredMockFindings = filteredMockFindings.filter(f => f.verified === true);
              } else if (filterType === 'unverified') {
                filteredMockFindings = filteredMockFindings.filter(f => f.verified === false);
              }
              
              if (sourceFilter !== 'all') {
                filteredMockFindings = filteredMockFindings.filter(f => f.detector_type === sourceFilter);
              }
            }

            // Sort findings: active + insights first, then active only, then inactive
            filteredMockFindings.sort((a, b) => {
              // Priority 1: Active + insights (verified=true AND has_insights=true)
              const aHasActiveAndInsights = a.verified === true && a.has_insights === true;
              const bHasActiveAndInsights = b.verified === true && b.has_insights === true;
              
              if (aHasActiveAndInsights && !bHasActiveAndInsights) return -1;
              if (!aHasActiveAndInsights && bHasActiveAndInsights) return 1;
              
              // Priority 2: Active (verified=true) comes before inactive (verified=false)
              if (a.verified === true && b.verified === false) return -1;
              if (a.verified === false && b.verified === true) return 1;
              
              // If both have same status, maintain original order
              return 0;
            });

            // Apply pagination
            const start = (pagination.page - 1) * pagination.per_page;
            const end = start + pagination.per_page;
            const paginatedFindings = filteredMockFindings.slice(start, end);
            const totalCount = filteredMockFindings.length;
            const lastPage = Math.ceil(totalCount / pagination.per_page);

            setFindings({
              data: paginatedFindings,
              pagination: {
                page: pagination.page,
                per_page: pagination.per_page,
                last_page: lastPage,
                has_next: pagination.page < lastPage,
                has_previous: pagination.page > 1,
                total: totalCount
              }
            });
            
            setFilteredFindings(paginatedFindings);
            setPagination({
              page: pagination.page,
              per_page: pagination.per_page,
              last_page: lastPage,
              has_next: pagination.page < lastPage,
              has_previous: pagination.page > 1,
              total: totalCount
            });
            
            setError(null);
        } else {
          // For other integration types, make API call
          let response;
          try {
            const requestConfig = {
              method: requestMethod,
              url: `${ENV.API_URL}${endpointUrl}`,
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${localStorage.getItem('jwt')}`,
              },
            };

            if (requestMethod === 'POST' && requestBody) {
              requestConfig.data = requestBody;
            } else if (requestMethod === 'GET') {
              requestConfig.params = {
                page: pagination.page,
                per_page: pagination.per_page
              };
            }

            response = await axios(requestConfig);

            if (!response.data?.data || response.data.data.length === 0) {
              setError(null); // Clear any previous errors
              setFindings({ data: [], pagination: { has_next: false, last_page: 1, page: 1, per_page: 50, total: 0 } });
              setFilteredFindings([]);
              return;
            }

            setFindings(response.data);
            setFilteredFindings(response.data.data);
            // Store all findings data for filter count calculation (if available)
            // For API responses, we only have current page, so use pagination.total for counts
            setAllFindings(response.data.data || []);
            setPagination(response.data.pagination || {
              has_next: false,
              last_page: 1,
              page: 1,
              per_page: 50,
              total: 0,
            });
          } catch (apiError) {
            // For other integration types, show error
            console.error("Error fetching findings:", apiError);
            const backendMessage = extractBackendMessage(apiError, "Something went wrong! Please try again later.");
            setError(backendMessage);
            showToast(backendMessage, "error");
          }
        }

      } catch (error) {
        console.error("Error in loadFindingsData:", error);
        const backendMessage = extractBackendMessage(error, "Something went wrong! Please try again later.");
        setError(backendMessage);
        showToast(backendMessage, "error");
      } finally {
        setLoading(false);
      }
    };
    
    loadFindingsData();
  }, [
    integrationType, 
    integrationId, 
    integrationConfig, 
    pagination.page, 
    pagination.per_page, 
    filterType, 
    showToast,
    // Add filter dependencies when filtersHook is provided
    filtersHook?.filterType,
    filtersHook?.sourceFilter,
    // Add integration and name so mock data can use them when available
    integration,
    name
  ]);

  // Simple loadFindings function for external calls (like from actions)
  const loadFindings = useCallback(async () => {
    if (!integrationType || !integrationId || !integrationConfig) return;
    
    // Trigger a re-render by updating a counter or timestamp
    setPagination(prev => ({ ...prev }));
  }, [integrationType, integrationId, integrationConfig]);

  useEffect(() => {
    if (findings.data) {
      let filtered = [...findings.data];

      if (filtersHook) {
        // When using filtersHook, backend handles filtering, so use data as-is
        // Only apply source filter client-side if it's not included in backend request
        if (filtersHook.sourceFilter !== 'all' && !integrationConfig?.filterConfig?.serverSideSourceFiltering) {
          filtered = filtered.filter(finding => finding.detector_type === filtersHook.sourceFilter);
        }
      } else {
        // Legacy client-side filtering when filtersHook is not used
        // Apply verification filter
        if (filterType === 'verified') {
          filtered = filtered.filter(finding => finding.verified === true);
        } else if (filterType === 'unverified') {
          filtered = filtered.filter(finding => finding.verified === false);
        }

        // Check if current source filter is still available for the current status filter
        if (sourceFilter !== 'all') {
          const availableSources = [...new Set(filtered.map(finding => finding.detector_type))];
          if (!availableSources.includes(sourceFilter)) {
            setSourceFilter('all');
          } else {
            // Apply source filter
            filtered = filtered.filter(finding => finding.detector_type === sourceFilter);
          }
        }
      }

      // Sort findings: active + insights first, then active only, then inactive
      // For GitHub/S3: verified=true means active, verified=false means unvalidated/inactive
      filtered.sort((a, b) => {
        // Priority 1: Active + insights (verified=true AND has_insights=true)
        const aHasActiveAndInsights = a.verified === true && a.has_insights === true;
        const bHasActiveAndInsights = b.verified === true && b.has_insights === true;
        
        if (aHasActiveAndInsights && !bHasActiveAndInsights) return -1;
        if (!aHasActiveAndInsights && bHasActiveAndInsights) return 1;
        
        // Priority 2: Active (verified=true) comes before inactive (verified=false)
        if (a.verified === true && b.verified === false) return -1;
        if (a.verified === false && b.verified === true) return 1;
        
        // If both have same status, maintain original order
        return 0;
      });

      setFilteredFindings(filtered);
    }
  }, [findings, filterType, sourceFilter, filtersHook?.filterType, filtersHook?.sourceFilter, integrationConfig]);

  // Update checkbox indeterminate state
  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.source-dropdown-container')) {
        setShowSourceDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Function to update individual finding status (for auto-fix polling)
  const updateFindingStatus = useCallback((findingId, statusUpdate) => {
    setFindings(prev => {
      if (!prev.data) return prev;
      
      const updatedData = prev.data.map(finding => {
        const currentFindingId = finding.id || String(finding.scan_id || '');
        if (currentFindingId === findingId) {
          return {
            ...finding,
            remediation_status: statusUpdate.status,
            pull_request_url: statusUpdate.pull_request_url || finding.pull_request_url
          };
        }
        return finding;
      });
      
      return {
        ...prev,
        data: updatedData
      };
    });

    // Also update filtered findings
    setFilteredFindings(prev => {
      return prev.map(finding => {
        const currentFindingId = finding.id || String(finding.scan_id || '');
        if (currentFindingId === findingId) {
          return {
            ...finding,
            remediation_status: statusUpdate.status,
            pull_request_url: statusUpdate.pull_request_url || finding.pull_request_url
          };
        }
        return finding;
      });
    });
  }, []);

  return {
    // Data
    findings,
    filteredFindings,
    allFindings, // All findings (useful for filter count calculations)
    loading,
    error,
    integration,
    name,
    
    // Filtering
    filterType,
    sourceFilter,
    showSourceDropdown,
    setShowSourceDropdown,
    filterOptions,
    setFilterOptions,
    handleFilterChange,
    handleSourceFilterChange,
    getUniqueSources,
    
    // Pagination
    pagination,
    setPagination,
    handlePrevPage,
    handleNextPage,
    handlePerPageChange,
    
    // Selection
    selected,
    setSelected,
    rowErrors,
    setRowErrors,
    revealedRows,
    toggleSelect,
    toggleSelectAll,
    toggleReveal,
    clearRowErrors,
    allSelected,
    selectedCount,
    isIndeterminate,
    selectedFindings,
    visibleIds,
    getFindingId,
    selectAllCheckboxRef,
    
    // Actions
    loadFindings,
    fetchIntegrationData,
    updateFindingStatus
  };
};